import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { distinctUntilChanged, skip } from 'rxjs/operators';
import { CurrentUserService } from './currentuser.service';
import { LocationFilterService } from './location-filter.service';
import { ModuleNotificationService } from './module-notification.service';
import { RealTimeEventType } from '@core/enums/real-time-event-type.enum';
import { UserType } from '@core/enums/type.enum';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: signalR.HubConnection | undefined;

  public appointmentAdded$ = new Subject<any>();
  public appointmentUpdated$ = new Subject<any>();
  public orderChanged$ = new Subject<any>();
  public userNotificationReceived$ = new Subject<any>();
  public userTaskAdded$ = new Subject<any>();
  public userMessageAdded$ = new Subject<any>();
  public patientAdded$ = new Subject<any>();
  public invoiceAdded$ = new Subject<any>();
  public userEvent$ = new Subject<any>();

  private currentJoinedLocationIds: string[] = [];
  private currentJoinedUserId: string | null = null;

  constructor(
    private currentUserService: CurrentUserService,
    private locationFilterService: LocationFilterService,
    private moduleNotificationService: ModuleNotificationService
  ) {
    this.currentUserService.IsUserLoggedIn.pipe(distinctUntilChanged()).subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        const user = this.currentUserService.get();
        if (user) {
          this.startConnection(user);
        }
      } else {
        this.stopConnection();
      }
    });

    this.locationFilterService.locationFilter$
      .pipe(
        skip(1),
        distinctUntilChanged((a, b) => a.join() === b.join())
      )
      .subscribe(() => {
        this.syncLocationGroups();
        this.moduleNotificationService.get();
      });
  }

  public startConnection(currentUser?: any): void {
    const user = currentUser || this.currentUserService.get();
    if (
      this.hubConnection &&
      this.hubConnection.state === signalR.HubConnectionState.Connected
    ) {
      this.joinGroups(user);
      return;
    }

    if (!user || !user.jwtToken) {
      return;
    }

    const hubUrl = `${environment.hubUrl}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => user.jwtToken
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onreconnected(() => {
      this.currentJoinedLocationIds = [];
      this.currentJoinedUserId = null;
      const u = this.currentUserService.get();
      if (u) {
        this.joinGroups(u);
      }
    });

    this.hubConnection
      .start()
      .then(() => {
        this.joinGroups(user);
      })
      .catch();

    this.addListeners();
  }

  private joinGroups(currentUser: any): void {
    if (!this.hubConnection) return;

    this.syncLocationGroups();

    // Join user group (for notifications, messages, tasks)
    if (currentUser.refId) {
      const userIdStr = currentUser.refId.toString();
      if (this.currentJoinedUserId === userIdStr) {
        return;
      }
      this.hubConnection.invoke('JoinUserGroup', userIdStr)
        .then(() => {
          this.currentJoinedUserId = userIdStr;
        })
        .catch(err => console.error(err));
    }
  }

  public syncLocationGroups(): void {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) return;

    const nextIds = (this.locationFilterService.getLocations() ?? []).filter(id => id && id !== '0');
    const currentIds = [...this.currentJoinedLocationIds];

    // Leave location groups that are no longer selected
    currentIds.forEach((id) => {
      if (!nextIds.includes(id)) {
        this.leaveLocationGroup(id);
      }
    });

    // Join location groups that are newly selected
    nextIds.forEach((id) => {
      if (!currentIds.includes(id)) {
        this.joinLocationGroup(id);
      }
    });
  }

  public joinLocationGroup(locationId: string): void {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) return;
    if (!locationId || locationId === '0') return;
    this.hubConnection.invoke('JoinLocationGroup', locationId.toString())
      .then(() => {
        this.currentJoinedLocationIds.push(locationId);
      })
      .catch(err => console.error(err));
  }

  public leaveLocationGroup(locationId: string): void {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) return;
    if (!locationId || locationId === '0') return;
    this.hubConnection.invoke('LeaveLocationGroup', locationId.toString())
      .then(() => {
        this.currentJoinedLocationIds = this.currentJoinedLocationIds.filter(id => id !== locationId);
      })
      .catch(err => console.error(err));
  }

  private addListeners(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on('Appointment', (data: any) => {
      const eventType = data?.eventType || data?.EventType;
      if (
        eventType === RealTimeEventType.AppointmentAdded
      ) {
        this.appointmentAdded$.next(data);
        this.moduleNotificationService.get();
      } else if (
        eventType === RealTimeEventType.AppointmentUpdated
      ) {
        this.appointmentUpdated$.next(data);
      } else if (
        eventType === RealTimeEventType.AppointmentOrderChanged
      ) {
        this.orderChanged$.next(data);
      }
    });


    this.hubConnection.on('User', (data: any) => {
      this.userEvent$.next(data);
      const eventType = data?.eventType || data?.EventType;
      if (eventType === RealTimeEventType.UserNotificationReceived) {
        this.userNotificationReceived$.next(data);
      } else if (eventType === RealTimeEventType.UserTaskAdded) {
        this.userTaskAdded$.next(data);
      } else if (eventType === RealTimeEventType.UserMessageAdded) {
        this.userMessageAdded$.next(data);
        this.moduleNotificationService.get();
      } else if (
        eventType === RealTimeEventType.PatientAdded
      ) {
        this.patientAdded$.next(data);
        this.moduleNotificationService.get();
      } else if (
        eventType === RealTimeEventType.InvoiceAdded
      ) {
        this.invoiceAdded$.next(data);
        this.moduleNotificationService.get();
      }
    });
  }

  public isFilterMatch(
    eventModel: any,
    activeLocationIds?: string[],
    activePractitionerIds?: string | string[]
  ): boolean {
    if (!eventModel) return true;

    const evLoc = eventModel.locationIdEnc || eventModel.LocationIdEnc;
    const evPrac = eventModel.practitionerIdEnc || eventModel.PractitionerIdEnc;

    let locList: string[] = [];
    if (activeLocationIds && activeLocationIds.length > 0) {
      locList = activeLocationIds.filter((id) => id && id !== '0');
    } else {
      locList = (this.locationFilterService.getLocations() || []).filter(
        (id) => id && id !== '0'
      );
    }

    if (locList.length > 0 && evLoc) {
      const matchLoc = locList.some(
        (id) => id.toLowerCase() === evLoc.toLowerCase()
      );
      if (!matchLoc) {
        return false;
      }
    }

    let pracList: string[] = [];
    if (Array.isArray(activePractitionerIds)) {
      pracList = activePractitionerIds.filter((id) => id && id !== '0');
    } else if (
      typeof activePractitionerIds === 'string' &&
      activePractitionerIds &&
      activePractitionerIds !== '0'
    ) {
      pracList = activePractitionerIds
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    if (pracList.length === 0) {
      const currentUser = this.currentUserService.get();
      if (
        currentUser?.userTypeId === UserType.Practitioner &&
        currentUser?.practitionerId
      ) {
        pracList = [currentUser.practitionerId];
      }
    }

    if (pracList.length > 0 && evPrac) {
      const matchPrac = pracList.some(
        (id) => id.toLowerCase() === evPrac.toLowerCase()
      );
      if (!matchPrac) {
        return false;
      }
    }

    return true;
  }

  public stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop();
      this.currentJoinedLocationIds = [];
      this.currentJoinedUserId = null;
    }
  }
}
